import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStore } from "@/lib/store";
import { useSiteContent } from "@/lib/siteContent";
import { createCheckoutSession, startShopifyCheckout } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Lock, ArrowRight, ShieldCheck, Truck, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function CheckoutPage() {
  const { state, cart, format, country } = useStore();
  const { checkout: checkoutCopy } = useSiteContent();
  const navigate = useNavigate();
  const [totals, setTotals] = useState({ subtotal: 0, shipping: 0, tax: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [sessionLoaded, setSessionLoaded] = useState(false);

  // Wait for cart to hydrate from the store before deciding to redirect.
  // `cart === null` = still loading; `cart.line_items === []` = truly empty.
  useEffect(() => {
    if (cart === null || cart === undefined) return; // still loading
    const items = cart.line_items || [];
    if (items.length === 0) {
      navigate("/shop");
      return;
    }
    if (sessionLoaded) return;
    setSessionLoaded(true);
    setLoading(true);
    createCheckoutSession(state.guestId)
      .then((sess) => setTotals(sess || {}))
      .catch((err) => {
        console.warn("[checkout] createCheckoutSession failed", err?.response?.status);
      })
      .finally(() => setLoading(false));
  }, [cart, navigate, sessionLoaded, state.guestId]);

  const proceedToShopify = async () => {
    setRedirecting(true);
    try {
      const res = await startShopifyCheckout(state.guestId, country);
      if (res?.checkout_url) {
        window.location.assign(res.checkout_url);
        return;
      }
      toast.error("Could not open checkout. Please try again.");
    } catch (err) {
      console.warn("[checkout] startShopifyCheckout failed", err?.response?.status, err?.response?.data);
      toast.error(err?.response?.data?.detail?.message || "Could not open checkout. Please try again.");
    } finally {
      setRedirecting(false);
    }
  };

  const items = cart?.line_items || [];

  // Cart is still loading from the store — show a graceful skeleton so users
  // never see a blank white screen.
  if (cart === null || cart === undefined) {
    return (
      <main
        data-testid="checkout-loading"
        className="max-w-[1280px] mx-auto px-5 sm:px-6 lg:px-10 py-16 sm:py-24 flex flex-col items-center justify-center text-center"
      >
        <Loader2 className="h-8 w-8 animate-spin text-[#C5A059] mb-4" />
        <div className="font-serif text-xl sm:text-2xl text-[#1A1918]">Preparing your checkout…</div>
        <div className="mt-2 text-sm text-[#1A1918]/60">One moment while we load your cart.</div>
      </main>
    );
  }

  return (
    <main data-testid="checkout-page" className="max-w-[1280px] mx-auto px-5 sm:px-6 lg:px-10 py-8 sm:py-10 lg:py-14">
      <div className="mb-6 sm:mb-10 flex items-center justify-between">
        <div>
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-[#C5A059]">Secure Checkout</div>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl mt-2">Review &amp; complete.</h1>
        </div>
        <div className="hidden sm:flex items-center gap-2 text-xs text-[#1A1918]/60 uppercase tracking-[0.16em]">
          <Lock className="h-3.5 w-3.5" /> SSL Encrypted · PCI Compliant
        </div>
      </div>

      <div className="grid lg:grid-cols-[1.15fr_1fr] gap-10 items-start">
        <div className="space-y-6">
          {/* Handoff card */}
          <div className="rounded-2xl border border-black/10 bg-[#FAF8F5] p-6 sm:p-8" data-testid="checkout-handoff-card">
            <div className="flex items-start gap-4">
              <div className="h-11 w-11 rounded-full bg-[#1A1918] text-[#FAF8F5] flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="h-5 w-5" />
              </div>
              <div>
                <div className="font-serif text-xl sm:text-2xl text-[#1A1918]">
                  {checkoutCopy.handoff_title}
                </div>
                <p className="mt-2 text-sm text-[#1A1918]/70 leading-relaxed max-w-md">
                  {checkoutCopy.handoff_body}
                </p>
              </div>
            </div>

            <div className="mt-6 grid sm:grid-cols-3 gap-3">
              {[
                { icon: ShieldCheck, label: checkoutCopy.feature_1_label, desc: checkoutCopy.feature_1_desc },
                { icon: Truck, label: checkoutCopy.feature_2_label, desc: checkoutCopy.feature_2_desc },
                { icon: RefreshCw, label: checkoutCopy.feature_3_label, desc: checkoutCopy.feature_3_desc },
              ].map((f) => (
                <div key={f.label} className="p-3 rounded-xl bg-[#F1ECE4]/70 border border-black/5">
                  <f.icon className="h-4 w-4 text-[#C5A059] mb-1.5" />
                  <div className="text-xs uppercase tracking-[0.14em] text-[#1A1918]">{f.label}</div>
                  <div className="text-[11px] text-[#1A1918]/60 mt-0.5">{f.desc}</div>
                </div>
              ))}
            </div>

            <Button
              data-testid="checkout-continue-button"
              onClick={proceedToShopify}
              disabled={redirecting || loading || items.length === 0}
              className="mt-6 w-full bg-[#1A1918] text-[#FAF8F5] hover:bg-[#0B0A08] rounded-xl py-6 text-sm uppercase tracking-[0.18em] disabled:opacity-40"
            >
              {redirecting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Preparing your order…</>
              ) : (
                <>Continue to Secure Payment <ArrowRight className="h-4 w-4 ml-2" /></>
              )}
            </Button>

            <div className="mt-3 text-[11px] text-[#1A1918]/55 text-center">
              Total shown may adjust slightly at checkout based on your shipping address (taxes &amp; delivery).
            </div>
          </div>

          {/* Trust bar */}
          <div className="text-[11px] text-[#1A1918]/55 flex flex-wrap gap-x-6 gap-y-1.5 justify-center sm:justify-between px-2">
            <span>© Scentodore</span>
            <span>Discreet packaging</span>
            <span>Free samples with every order</span>
            <span>Complimentary engraving available</span>
          </div>
        </div>

        <aside className="rounded-2xl border border-white/10 p-6 sd-dark sd-dark-noise relative overflow-hidden" data-testid="checkout-summary">
          <div className="absolute -top-16 -right-16 w-[220px] h-[220px] sd-amber-glow-dark rounded-full pointer-events-none" />
          <div className="relative">
            <div className="font-serif text-xl mb-4 text-[#FAF8F5]">Order Summary</div>
            <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
              {items.map((item, i) => (
                <div key={`${item.product?.id || "item"}-${item.size_label}-${i}`} className="flex items-center gap-3">
                  <div className="w-12 h-14 rounded-md bg-white/5" style={{ backgroundImage: `url(${item.product?.image_url})`, backgroundSize: "cover", backgroundPosition: "center" }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-[#FAF8F5]/60">{item.product?.number}</div>
                    <div className="font-serif text-sm text-[#FAF8F5] truncate">{item.product?.name}</div>
                    <div className="text-[11px] text-[#FAF8F5]/60">{item.size_label} × {item.quantity}</div>
                  </div>
                  <div className="text-sm text-[#FAF8F5]">{format(item.line_total)}</div>
                </div>
              ))}
            </div>

            {cart?.free_sample && (
              <div className="mt-4 p-3 rounded-lg border border-[#D4B278]/40 bg-[#D4B278]/10 text-xs">
                <div className="uppercase tracking-[0.14em] text-[#D4B278]">Free Sample — included</div>
                <div className="text-[#FAF8F5]/80 mt-0.5">A 2ml sample will be added to your order.</div>
              </div>
            )}

            <div className="h-px bg-white/10 my-5" />
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-[#FAF8F5]"><span>Subtotal</span><span>{format(totals.subtotal || 0)}</span></div>
              <div className="flex justify-between text-[#FAF8F5]/75"><span>Shipping</span><span className="text-[10px] uppercase tracking-[0.16em] text-[#FAF8F5]/60">Calculated at checkout</span></div>
              <div className="flex justify-between text-[#FAF8F5]/75"><span>Tax</span><span className="text-[10px] uppercase tracking-[0.16em] text-[#FAF8F5]/60">Calculated at checkout</span></div>
              <div className="flex justify-between text-base font-medium pt-2 border-t border-white/10 text-[#FAF8F5]"><span>Estimated Total</span><span data-testid="checkout-total">{format(totals.subtotal || 0)}</span></div>
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
}
